export class ScriptPlayerService {
  private audioContext: AudioContext | null = null;
  private analyser: AnalyserNode | null = null;
  private sourceNode: MediaElementAudioSourceNode | null = null;
  private audioVal: HTMLAudioElement | null = null;
  private rafId: number | null = null;
  private playbackRate: number = 1.0;

  // 콜백
  public onAudioLevel: (volume: number) => void = () => {};
  public onPlayEnd: () => void = () => {};
  public onError: (error: unknown) => void = () => {};
  public onProgress: (
    currentTime: number,
    duration: number,
    maxTimeReached: number,
  ) => void = () => {};

  private currentUrl: string | null = null;

  private maxTimeReached: number = 0;

  constructor() {}

  // 일시정지 토글 기능
  public togglePause() {
    if (!this.audioVal) return false;

    if (this.audioVal.paused) {
      // 재생 (Resume)
      if (this.audioContext && this.audioContext.state === "suspended") {
        this.audioContext.resume();
      }
      this.audioVal.play().catch((e) => console.error("Resume failed:", e));
      // 멈췄던 분석 루프 재가동
      this.startAnalysis();
      return false; // isPaused = false
    } else {
      // 일시정지 (Pause)
      this.audioVal.pause();

      // iOS/WebKit 고배속 재생 중 일시정지 시 버퍼 잔여음이 반복되는(메아리) 현상 방지
      // (Web Audio API를 사용하는 1배속 모드에서만 유효)
      if (this.audioContext && this.audioContext.state === "running") {
        this.audioContext.suspend();
      }

      this.onAudioLevel(0); // 입 다물기
      // 분석 루프는 startAnalysis 내부의 'if (paused) return'에 의해 자동 종료됨
      return true; // isPaused = true
    }
  }

  // 현재 일시정지 상태인지 확인 (Getter)
  public get isPaused(): boolean {
    return this.audioVal ? this.audioVal.paused : false;
  }

  // 재생 배속 설정
  public setPlaybackRate(rate: number) {
    const prevRate = this.playbackRate;
    this.playbackRate = rate;

    // 1.0 배속(Web Audio 사용)과 고배속(Native Audio 사용) 간 전환 시
    // 오디오 객체를 재생성해야 하므로 playAudio를 재호출하여 모드를 변경합니다.
    const wasNormal = prevRate === 1.0;
    const isNormal = rate === 1.0;

    if (wasNormal !== isNormal && this.audioVal && this.currentUrl) {
      const currentTime = this.audioVal.currentTime;
      // 재생 상태였는지 확인
      const wasPlaying = !this.audioVal.paused;

      this.playAudio(this.currentUrl, currentTime).then(() => {
        // 만약 이전에 일시정지 상태였다면 다시 멈춤
        if (!wasPlaying && this.audioVal) {
          this.audioVal.pause();
        }
      });
    } else {
      // 그 외의 경우(예: 1.5 -> 2.0)는 속도만 변경
      if (this.audioVal) {
        this.audioVal.playbackRate = rate;
      }
    }
  }

  // 시간 이동 (Seeking)
  public seekTo(time: number) {
    if (!this.audioVal) return;

    // 0 ~ maxTimeReached 사이로 제한(Clamp)
    const targetTime = Math.min(Math.max(0, time), this.maxTimeReached);

    // 현재 시간과 차이가 아주 작다면(0.5초 미만) 무시하여 연달아 클릭 시 발생하는 버벅임 방지
    if (Math.abs(this.audioVal.currentTime - targetTime) < 0.5) return;

    this.audioVal.currentTime = targetTime;
  }

  // 오디오 URL을 받아 재생 및 분석
  // startTime 인자 추가: 모드 전환 시 이어듣기 위함
  public async playAudio(url: string, startTime: number = 0) {
    this.stop(); // 기존 재생 중단

    // URL이 바뀌었으면 maxTimeReached 초기화, 같으면 유지(모드 전환 시)
    if (this.currentUrl !== url) {
      this.maxTimeReached = 0;
    }
    this.currentUrl = url;

    // 1배속일 때만 Web Audio API(립싱크) 사용
    // iOS에서 배속 재생 시 Web Audio API 사용하면 끊김/스킵 발생하므로 Native Audio 사용
    const useWebAudio = this.playbackRate === 1.0;

    if (useWebAudio) {
      // AudioContext 초기화 (브라우저 정책상 사용자 클릭 후 생성 권장)
      if (!this.audioContext) {
        const AudioContextClass =
          window.AudioContext ||
          (window as unknown as { webkitAudioContext: typeof AudioContext })
            .webkitAudioContext;
        this.audioContext = new AudioContextClass();
      }
      if (this.audioContext.state === "suspended") {
        await this.audioContext.resume();
      }
    }

    // HTML Audio Element 생성 (스트리밍 재생용)
    this.audioVal = new Audio();
    // URL을 할당하기 전에 설정을 먼저 해야 안정적입니다.
    this.audioVal.crossOrigin = "anonymous"; // CORS 문제 방지
    this.audioVal.src = url;
    this.audioVal.playbackRate = this.playbackRate; // 저장된 배속 적용
    this.audioVal.currentTime = startTime; // 시작 시간 설정

    // 이벤트 핸들러
    this.audioVal.onended = () => {
      this.stopAnalysis();
      this.onPlayEnd();
    };

    this.audioVal.onerror = (e) => {
      this.stop(); // 재생 및 분석 정지
      this.onError(e); // 에러 발생 알림
    };

    let lastProgressTime = 0;
    this.audioVal.ontimeupdate = () => {
      if (!this.audioVal) return;

      // React 상태 업데이트 빈도 제한 (100ms, 약 10fps)
      const now = Date.now();
      if (now - lastProgressTime < 100) return;
      lastProgressTime = now;

      if (this.audioVal.currentTime > this.maxTimeReached) {
        this.maxTimeReached = this.audioVal.currentTime;
      }
      this.onProgress(
        this.audioVal.currentTime,
        this.audioVal.duration,
        this.maxTimeReached,
      );
    };

    if (useWebAudio && this.audioContext) {
      // Web Audio API 연결
      // Audio Element -> MediaElementSource -> Analyser -> Destination(스피커)
      this.sourceNode = this.audioContext.createMediaElementSource(
        this.audioVal,
      );
      this.analyser = this.audioContext.createAnalyser();
      this.analyser.fftSize = 256;

      this.sourceNode.connect(this.analyser);
      this.analyser.connect(this.audioContext.destination);
    } else {
      // 고배속 모드: Web Audio 연결 안 함 (Native 재생으로 끊김 방지)
      this.sourceNode = null;
      this.analyser = null;
    }

    // 재생 및 분석 시작
    try {
      await this.audioVal.play();
      this.startAnalysis();
    } catch (err) {
      console.error("Autoplay failed:", err);
    }
  }

  // GeminiLiveService와 동일한 Time Domain 분석 로직 적용
  private startAnalysis() {
    let lastTime = 0;
    const update = (time: number) => {
      if (!this.audioVal || this.audioVal.paused) return; // 일시정지 시 루프 종료

      // 30fps 제한
      if (time - lastTime >= 33) {
        lastTime = time;

        if (this.analyser) {
          // Web Audio 모드 (1배속): 실제 오디오 파형 분석
          const bufferLength = this.analyser.frequencyBinCount;
          const dataArray = new Uint8Array(bufferLength);
          this.analyser.getByteTimeDomainData(dataArray);

          let sum = 0;
          for (let i = 0; i < bufferLength; i++) {
            const x = dataArray[i] - 128;
            sum += x * x;
          }
          const rms = Math.sqrt(sum / bufferLength);
          const volume = Math.min(1, rms / 40);
          this.onAudioLevel(volume);
        } else {
          // Native 모드 (고배속): 가짜 립싱크 (Fake Lip-sync)
          // Random하지만 약간의 패턴을 줘서 자연스럽게
          // 0.0 ~ 0.6 사이의 값으로 입을 뻐끔거림
          const fakeVolume = Math.random() * 0.6;
          this.onAudioLevel(fakeVolume);
        }
      }
      this.rafId = requestAnimationFrame(update);
    };
    this.rafId = requestAnimationFrame(update);
  }

  private stopAnalysis() {
    if (this.rafId) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
    this.onAudioLevel(0); // 입 다물기
  }

  public stop() {
    this.stopAnalysis();

    if (this.audioVal) {
      this.audioVal.pause(); // playAndAudio의 catch 블록 등에서 호출 시 에러 방지용으로 pause 먼저
      // src 비우기 전 이벤트 핸들러 해제 (안전장치)
      this.audioVal.onended = null;
      this.audioVal.onerror = null;
      this.audioVal.ontimeupdate = null;

      this.audioVal.src = "";
      this.audioVal = null;
    }

    // 노드 연결 해제 (메모리 누수 방지)
    if (this.sourceNode) {
      this.sourceNode.disconnect();
      this.sourceNode = null;
    }
    // Analyser는 재사용 가능하므로 굳이 null 처리 안 해도 됨 (Context 종속)
    // 하지만 모드 전환 시 명확함을 위해 analyser도 null 처리 할 수 있으나
    // constructor에서 생성하는 것이 아니므로 여기선 둠. playAudio에서 매번 생성함.
    if (this.analyser) {
      this.analyser.disconnect();
      this.analyser = null;
    }
  }
}

export const scriptPlayer = new ScriptPlayerService();
